/**
 * Адреса экранов (тикет T18b).
 *
 * Своя разборка вместо готовой библиотеки: адресов у нас пять, а библиотека тянет за
 * собой своё понимание истории, переходов и вложенности — и своё поведение при письме
 * справа налево. Пять адресов дешевле разобрать самим, чем спорить с чужими правилами.
 *
 * Главное правило раздела «Настройки» (§ 14, критерий 13): **в адресе нет человека**.
 * Появись там номер Участника, кто-нибудь непременно подставил бы чужой — и разбираться
 * пришлось бы уже с тем, почему это сработало.
 */

export type Route =
  /** Ещё не вошли. Пространство могли назвать в адресе — тогда его не переспрашивают. */
  | { readonly name: 'signIn'; readonly spaceSlug: string | null }
  /**
   * Переход по Приглашению. Пространство и одноразовый ключ зашиты в саму ссылку:
   * человек, которого зовут в фирму, ещё не знает ни адреса Пространства, ни своего
   * пароля — спрашивать их у него на этом шаге не у кого.
   */
  | { readonly name: 'join'; readonly spaceSlug: string; readonly token: string }
  /** Рабочее место: Канал открыт или ещё не выбран. */
  | { readonly name: 'channel'; readonly spaceSlug: string; readonly channelId: string | null }
  /** Свои Настройки. Чужих в адресе не назвать — их здесь просто нет. */
  | { readonly name: 'settings'; readonly spaceSlug: string };

const clean = (value: string): string => decodeURIComponent(value).trim();

/**
 * Разобрать путь. Непонятный путь ведёт на рабочее место Пространства, а не на страницу
 * «не найдено»: человек, ошибшийся в адресе, хочет попасть к себе, а не прочитать про
 * свою ошибку.
 */
export const routeOf = (path: string): Route => {
  const parts = path.split('/').filter((one) => one.length > 0);
  const first = parts[0];
  if (!first) return { name: 'signIn', spaceSlug: null };

  const spaceSlug = clean(first);
  if (!spaceSlug) return { name: 'signIn', spaceSlug: null };

  const second = parts[1];
  if (second === 'settings') return { name: 'settings', spaceSlug };
  if (second === 'join') {
    const token = parts[2] ? clean(parts[2]) : '';
    // Ссылка без ключа Приглашением не является: вести человека на форму, которая всё
    // равно упрётся в отказ, — значит потратить его время на заведомо мёртвый шаг.
    if (token) return { name: 'join', spaceSlug, token };
    return { name: 'signIn', spaceSlug };
  }
  if (second === 'channels') {
    const third = parts[2];
    return { name: 'channel', spaceSlug, channelId: third ? clean(third) : null };
  }
  return { name: 'channel', spaceSlug, channelId: null };
};

/** Собрать путь обратно — чтобы адрес в строке браузера писало одно место, а не пять. */
export const pathOf = (route: Route): string => {
  switch (route.name) {
    case 'signIn':
      return route.spaceSlug ? `/${encodeURIComponent(route.spaceSlug)}` : '/';
    case 'settings':
      return `/${encodeURIComponent(route.spaceSlug)}/settings`;
    case 'join':
      return `/${encodeURIComponent(route.spaceSlug)}/join/${encodeURIComponent(route.token)}`;
    default:
      return route.channelId
        ? `/${encodeURIComponent(route.spaceSlug)}/channels/${encodeURIComponent(route.channelId)}`
        : `/${encodeURIComponent(route.spaceSlug)}`;
  }
};
